import type {
  BatteryArbitrageDto,
  BatteryArbitrageStatus,
} from "~/api/dto/battery-arbitrage.dto"

import type { BatterySetupStep } from "./setup-steps"

export type BatteryArbitrageTone = `busy` | `info` | `problem` | `ready`

export type BatteryArbitrageStatusView = {
  tone: BatteryArbitrageTone
  headline: string
  detail: string
  /** The step a seller opens to fix this condition, when one does. */
  fixStep: BatterySetupStep | null
  /** Label for the action that re-runs the calculation, when offering one helps. */
  actionLabel: string | null
  /** Short note for the Setup rail. Null while nothing needs saying there. */
  railHint: string | null
}

type ReasonCopy = {
  headline: string
  detail: string
  fixStep: BatterySetupStep | null
}

/**
 * Gateway reason codes, from
 * `gateway/src/module-v2/battery-proposal/battery-arbitrage-load.ts` and
 * `battery-arbitrage-provider.service.ts`. Canonical consumption validation and
 * the provider boundary each have their own name for a missing hourly shape,
 * and both mean the same thing to a seller.
 */
const REASONS: Record<string, ReasonCopy> = {
  measured_channels_ambiguous: {
    headline: `The meter channels need clarification`,
    detail: `The measured readings do not clearly distinguish grid imports from exports or net usage. Select a suitable Consumption Profile or enter usage for a generalized scenario.`,
    fixStep: `utility`,
  },
  measured_intervals_incomplete: {
    headline: `Measured usage does not cover a full year`,
    detail: `Select complete measured usage or enter annual or monthly usage for a generalized scenario.`,
    fixStep: `utility`,
  },
  measured_intervals_imputed: {
    headline: `Some measured readings were filled in`,
    detail: `Select complete measured usage or enter annual or monthly usage for a generalized scenario.`,
    fixStep: `utility`,
  },
  missing_export_readings: {
    headline: `Existing solar exports are missing`,
    detail: `The selected usage cannot establish existing export credits. Review the Consumption Profile and existing-solar answer in Battery Savings.`,
    fixStep: `arbitrage`,
  },
  tariff_applicability_missing: {
    headline: `The rate plan needs more billing inputs`,
    detail: `This rate plan needs allowance or eligibility information before it can be calculated. Confirm the selected plan and review its missing billing inputs.`,
    fixStep: `utility`,
  },
  unsupported_tariff_formula: {
    headline: `A rate-plan charge cannot be calculated`,
    detail: `The selected calculation method cannot represent a charge formula on this rate plan. The plan can stay selected.`,
    fixStep: `utility`,
  },
  tariff_version_mismatch: {
    headline: `The rate schedule needs an update`,
    detail: `The available rate schedule does not match the calculation period. Confirm the rate plan; an updated schedule is needed for this estimate.`,
    fixStep: `utility`,
  },
  unsupported_billing_period: {
    headline: `The billing period cannot be modeled`,
    detail: `The rate plan's billing period is not supported by the selected calculation method. The plan can stay selected.`,
    fixStep: `utility`,
  },
  public_profile_unavailable: {
    headline: `No representative usage profile is available`,
    detail: `No maintained public profile matches this location. Check the site address or select suitable measured usage.`,
    fixStep: `site`,
  },
  local_rate_unavailable: {
    headline: `No maintained rate scenario is available`,
    detail: `A local rate scenario is not available for this plan, and provider billing could not be used. Your Utility Company and rate plan can stay selected.`,
    fixStep: `utility`,
  },
  consumption_missing: {
    headline: `Savings need the homeowner's usage`,
    detail: `Pick a Consumption Profile for this Proposal and the calculation starts on its own.`,
    fixStep: `utility`,
  },
  measurement_basis_unsuitable: {
    headline: `This usage can't stand in for grid purchases`,
    detail: `Arbitrage models what the home buys from the grid. Answer the existing-solar question below, or select measured grid-import usage instead.`,
    fixStep: null,
  },
  load_shape_missing: {
    headline: `Savings need hour-by-hour usage`,
    detail: `The selected usage has no hourly shape behind it. Select a profile with interval data, or enter annual or monthly usage so an hourly shape can be scaled to it.`,
    fixStep: `utility`,
  },
  missing_load_shape: {
    headline: `Savings need hour-by-hour usage`,
    detail: `The selected usage has no hourly shape behind it. Select a profile with interval data, or enter annual or monthly usage so an hourly shape can be scaled to it.`,
    fixStep: `utility`,
  },
  missing_equipment_quantity: {
    headline: `Savings need the battery quantity`,
    detail: `Set how many batteries this Proposal includes.`,
    fixStep: `equipment`,
  },
  missing_usable_capacity: {
    headline: `Savings need usable capacity`,
    detail: `The selected battery has no usable kWh on record, so there is nothing to charge and discharge.`,
    fixStep: `equipment`,
  },
  missing_rated_power: {
    headline: `Savings need rated power`,
    detail: `The selected battery has no rated kW on record, so charge and discharge cannot be limited honestly.`,
    fixStep: `equipment`,
  },
  missing_efficiency: {
    headline: `Savings need round-trip efficiency`,
    detail: `The selected battery has no round-trip efficiency on record. Nothing is assumed in its place.`,
    fixStep: `equipment`,
  },
  unsupported_tariff: {
    headline: `This rate plan isn't supported yet`,
    detail: `The current calculation cannot model this rate plan with the available inputs. Your Utility Company and rate plan can stay selected.`,
    fixStep: `utility`,
  },
  provider_account_missing: {
    headline: `Savings need a confirmed utility account`,
    detail: `Confirm the Utility Company and rate plan so the site has a provider account to price against.`,
    fixStep: `utility`,
  },
  timezone_mismatch: {
    headline: `Usage and rate plan disagree on the time zone`,
    detail: `Hourly usage and hourly rates have to line up. Recheck the site address and the selected usage.`,
    fixStep: `site`,
  },
  provider_calculation_failed: {
    headline: `The calculation didn't finish`,
    detail: `The utility rate provider did not return a usable bill. Nothing is wrong with the Proposal — retry.`,
    fixStep: null,
  },
}

const UNKNOWN_UNAVAILABLE: ReasonCopy = {
  headline: `Savings aren't available for this Proposal`,
  detail: `Something the calculation needs is missing or unsupported. Check equipment, usage, and the rate plan.`,
  fixStep: null,
}

export const savingsVisibilityView = {
  hidden: {
    headline: `Savings are hidden from the proposal`,
    detail: `Year 1 savings, the savings section, and the homeowner link and brochure stay off while Show on proposal is off. Turn it on to calculate current savings and show them.`,
  },
} as const

/**
 * A status this client does not know, or a `ready` that arrived without the
 * result it promises. Either means a gateway the client cannot read — a new
 * lifecycle state, or a body that is not the contract. Saying so beats a blank
 * panel, and beats indexing past the end of a lookup and crashing the Setup
 * rail for every seller with this group open.
 */
const UNREADABLE: BatteryArbitrageStatusView = {
  tone: `problem`,
  headline: `Savings status can't be read`,
  detail: `This Proposal's savings are in a state this page does not recognize. Reload; if it persists, the app is behind the API.`,
  fixStep: null,
  actionLabel: null,
  railHint: `Savings status unknown`,
}

/** Own-property lookup: a reason of `constructor` must not resolve up the chain. */
export const batteryArbitrageReasonCopy = (
  reason: string | null,
  fallback: ReasonCopy = UNKNOWN_UNAVAILABLE,
): ReasonCopy =>
  reason != null && Object.hasOwn(REASONS, reason) ? REASONS[reason] : fallback

const BY_STATUS: Record<
  Exclude<BatteryArbitrageStatus, `failed` | `ready` | `unavailable`>,
  BatteryArbitrageStatusView
> = {
  not_calculated: {
    tone: `info`,
    headline: `No savings calculated yet`,
    detail: `Savings calculate on their own once equipment, usage, and the rate plan are saved. There is nothing to press.`,
    fixStep: null,
    actionLabel: null,
    railHint: null,
  },
  pending: {
    tone: `busy`,
    headline: `Calculating savings`,
    detail: `A calculation is queued for the current inputs. This panel updates when it finishes.`,
    fixStep: null,
    actionLabel: null,
    railHint: `Calculating savings`,
  },
  running: {
    tone: `busy`,
    headline: `Calculating savings`,
    detail: `A calculation is running against the current inputs. This panel updates when it finishes.`,
    fixStep: null,
    actionLabel: null,
    railHint: `Calculating savings`,
  },
  outdated: {
    tone: `problem`,
    headline: `Savings are out of date`,
    detail: `The Proposal changed after the last calculation, so the saved savings no longer describe it and are not being shown or shared.`,
    fixStep: null,
    actionLabel: `Recalculate`,
    railHint: `Savings out of date`,
  },
}

/** What to tell the seller about the current lifecycle state, in one place. */
export const batteryArbitrageStatusView = (
  arbitrage: BatteryArbitrageDto,
): BatteryArbitrageStatusView => {
  if (arbitrage.status === `ready`) {
    if (arbitrage.result == null) {
      return UNREADABLE
    }
    return {
      tone: `ready`,
      headline: `Savings are current`,
      detail: `These figures come from the saved calculation for this Proposal's equipment, usage, and rate plan.`,
      fixStep: null,
      actionLabel: null,
      railHint: null,
    }
  }

  if (arbitrage.status === `failed`) {
    const copy = batteryArbitrageReasonCopy(
      arbitrage.reason,
      REASONS.provider_calculation_failed,
    )
    return {
      tone: `problem`,
      headline: copy.headline,
      detail: copy.detail,
      fixStep: copy.fixStep,
      actionLabel: `Retry calculation`,
      railHint: `Savings calculation failed`,
    }
  }

  if (arbitrage.status === `unavailable`) {
    const copy = batteryArbitrageReasonCopy(
      arbitrage.reason,
      UNKNOWN_UNAVAILABLE,
    )
    return {
      tone: `problem`,
      headline: copy.headline,
      detail: copy.detail,
      fixStep: copy.fixStep,
      // Retrying an unsupported tariff or absent equipment just repeats the
      // same answer. The fix is the input, so that is the only thing offered.
      actionLabel: null,
      railHint: `Savings unavailable`,
    }
  }

  return Object.hasOwn(BY_STATUS, arbitrage.status)
    ? BY_STATUS[arbitrage.status]
    : UNREADABLE
}
